import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Clock, MessageCircle, CalendarDays, Navigation } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/shared";
import { useBooking } from "@/components/BookingContext";
import { business, fullAddress, mapEmbedSrc, whatsappLink, routes } from "@/lib/site";
import { toast } from "sonner";
import { combine, localBusinessSchema, breadcrumbSchema } from "@/lib/schema";

export default function Contact() {
  const { openBooking } = useBooking();
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: "", message: "" });
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg =
      `Hello Dr. Satyam, I would like to enquire about an appointment.\n\n` +
      `Name: ${form.name || "-"}\n` +
      `Phone: ${form.phone || "-"}\n` +
      (form.email ? `Email: ${form.email}\n` : "") +
      `Preferred Date: ${form.date || "-"}\n` +
      (form.message ? `Message: ${form.message}` : "");
    window.open(whatsappLink(msg), "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp to send your enquiry…", {
      description: "If nothing opens, please call us at +91 81879 71516.",
    });
  };

  const jsonLd = combine(
    localBusinessSchema,
    breadcrumbSchema([
      { name: "Home", path: routes.home },
      { name: "Contact", path: routes.contact },
    ])
  );

  return (
    <>
      <SEO
        path={routes.contact}
        title="Contact Dr. Satyam Jaiswal — Chiropractor in Varanasi"
        description="Contact Dr. Satyam's Pain Healthcare in Sigra, Varanasi. Call, WhatsApp or book an appointment. Address, clinic timings and location map."
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="Get in Touch"
        title="Contact Dr. Satyam Jaiswal – Chiropractor in Varanasi"
        sub="Reach out to book an appointment or ask a question. We're happy to help you take the first step."
        breadcrumb={[{ label: "Contact Us" }]}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          {/* Details */}
          <Reveal>
            <h2 className="font-heading font-extrabold text-navy text-2xl sm:text-3xl">Clinic Details</h2>
            <p className="mt-2 font-semibold text-brand-orange">{business.clinicName}</p>
            <div className="mt-6 space-y-5">
              <div className="flex gap-4">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-navy/5 shrink-0"><MapPin className="h-5 w-5 text-brand-orange" /></div>
                <div>
                  <p className="font-semibold text-navy">Address</p>
                  <p className="text-ink text-sm leading-relaxed mt-0.5">{fullAddress}</p>
                  <p className="text-xs text-muted-foreground mt-1">Landmark: {business.address.landmark}</p>
                </div>
              </div>
              <a href={`tel:${business.phoneTel}`} className="flex gap-4 group" data-testid="contact-phone">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-navy/5 shrink-0"><Phone className="h-5 w-5 text-brand-orange" /></div>
                <div>
                  <p className="font-semibold text-navy">Phone</p>
                  <p className="text-ink text-sm mt-0.5 group-hover:text-brand-orange transition-colors">{business.phoneDisplay}</p>
                </div>
              </a>
              <div className="flex gap-4">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-navy/5 shrink-0"><Clock className="h-5 w-5 text-brand-orange" /></div>
                <div>
                  <p className="font-semibold text-navy">Clinic Timings</p>
                  {business.timings.map((t) => (
                    <p key={t.days} className="text-ink text-sm mt-0.5">{t.days}: <span className="font-medium">{t.hours}</span></p>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`tel:${business.phoneTel}`} data-testid="contact-call-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-navy/15 px-6 py-3 font-bold text-navy hover:bg-navy hover:text-white transition-colors">
                <Phone className="h-5 w-5" /> Call Now
              </a>
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-bold text-white hover:brightness-95 transition-[filter]">
                <MessageCircle className="h-5 w-5" /> WhatsApp
              </a>
              <button onClick={openBooking} data-testid="contact-book-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 font-bold text-white hover:bg-brand-orange-dark transition-colors">
                <CalendarDays className="h-5 w-5" /> Book Appointment
              </button>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={120}>
            <div className="bg-soft rounded-2xl p-8 shadow-sm">
              <h2 className="font-heading font-extrabold text-navy text-xl sm:text-2xl">Send an Enquiry</h2>
              <p className="text-sm text-ink mt-1.5">Submit the form and we'll continue on WhatsApp to confirm your appointment.</p>
              <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="contact-form">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Name</Label>
                  <Input id="c-name" required value={form.name} onChange={update("name")} placeholder="Your full name" data-testid="contact-name-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="c-phone">Phone Number</Label>
                    <Input id="c-phone" required type="tel" value={form.phone} onChange={update("phone")} placeholder="+91" data-testid="contact-phone-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="c-date">Preferred Date</Label>
                    <Input id="c-date" type="date" value={form.date} onChange={update("date")} data-testid="contact-date-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-email">Email <span className="text-muted-foreground">(optional)</span></Label>
                  <Input id="c-email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" data-testid="contact-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-message">Message</Label>
                  <Textarea id="c-message" rows={4} value={form.message} onChange={update("message")} placeholder="Briefly describe your concern" data-testid="contact-message-input" />
                </div>
                <Button type="submit" data-testid="contact-submit-btn" className="w-full h-12 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold text-base transition-colors">
                  <MessageCircle className="h-5 w-5 mr-2" /> Send on WhatsApp
                </Button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="bg-soft pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <h2 className="font-heading font-extrabold text-navy text-2xl">Find Us in Varanasi</h2>
            <a href={business.socials.google} target="_blank" rel="noopener noreferrer" data-testid="get-directions" className="inline-flex items-center gap-2 font-bold text-navy hover:text-brand-orange transition-colors">
              <Navigation className="h-4 w-4" /> Get Directions
            </a>
          </div>
          <div className="overflow-hidden rounded-2xl shadow-sm border border-border">
            <iframe
              title="Dr. Satyam's Pain Healthcare location map"
              src={mapEmbedSrc}
              className="w-full h-[380px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-embed"
            />
          </div>
        </div>
      </section>
    </>
  );
}
